import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, inject, InjectionToken, PLATFORM_ID } from '@angular/core';
import { AbilityUtils } from '@core/shared/ability';
import { ConfigService } from '@core/shared/config';
import { FeatureRegistry, FeatureStatusService, FeatureType, OptInFeatureManager, OptInFeatureScope } from '@core/shared/feature';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, map, take } from 'rxjs';
import { AccessState } from '../../access/access/access.state';
import { DevToolsRegistry } from '../../dev-tools';
import { ScopeState } from '../../scope/scope.state';
import { takeWhileAlive } from '@core/frontend-shared/utils';
import * as i0 from "@angular/core";
import * as i1 from "@core/shared/config";
import * as i2 from "../../dev-tools";
// located in own file which is not exported from index, so it is "internal"
// must not import AccessService! would cause circular dependency!
export const BASE_FEATURES_PROVIDER = /*#__PURE__*/new InjectionToken("Base Features Provider");
/**
 * NOTE: FeatureRegistry is mostly considered internal. To interact with plugins, please inject FeatureService and use its API.
 */
export let FrontendFeatureRegistry = /*#__PURE__*/(() => {
  class FrontendFeatureRegistry extends FeatureRegistry {
    constructor(config, debug, baseFeatures, platformId) {
      super(config);
      this.config = config;
      this.platformId = platformId;
      this.destroyRef = inject(DestroyRef);
      this.featureStatus = new FeatureStatusService();
      this.abilities$ = inject(Store).select(AccessState.abilities);
      this.role$ = inject(Store).select(AccessState.role);
      this.activatedFeatures$ = inject(Store).select(AccessState.activatedFeatures);
      // list of all Features that are enabled + loaded
      this._loadedPlugins$ = new BehaviorSubject([]);
      this.scope$ = inject(Store).select(ScopeState.scope);
      this.accessInfo$ = combineLatest({
        role: this.role$,
        currentScope: this.scope$,
        abilities: this.abilities$.pipe(map(abilityMap => AbilityUtils.abilitiesMapToGrantedList(abilityMap)))
      });
      this.setup(baseFeatures);
      this.initializePlugins();
      debug.exposeDebugFunction(['Features', 'Inspect Registry'], () => {
        return this.getBaseRegistry().pipe(take(1), map(bases => {
          const featureMap = bases.reduce((ftMap, base) => {
            ftMap[base.resolveFeatureInfoFromStatic().context.featureId] = {
              ...base.resolveFeatureInfoFromStatic(),
              definition: base.getSharedDefinitionCached()
            };
            return ftMap;
          }, {});
          const orderedFeatureMap = {};
          const props = Object.keys(featureMap).sort();
          props.forEach(p => orderedFeatureMap[p] = featureMap[p]);
          return orderedFeatureMap;
        }));
      });
      debug.exposeDebugFunction(['Features', 'Inspect Plugin Registry'], () => {
        return this.getPluginRegistry().pipe(take(1), map(featurePlugins => {
          return featurePlugins.map(feature => {
            const status = this.featureStatus.get(feature);
            return {
              instance: feature,
              context: feature.getFeatureContext(),
              featureInfo: feature.__info,
              definition: feature.getFeatureFunctionalityCached(),
              loaded: status.loaded,
              status
            };
          });
        }));
      });
    }
    setupPluginStatusCalculation() {
      combineLatest({
        registeredFeatures: this.getPluginRegistry(),
        accessInfo: this.accessInfo$,
        activatedOptInFeatures: this.activatedFeatures$
      }).subscribe(({
        registeredFeatures,
        accessInfo,
        activatedOptInFeatures
      }) => {
        registeredFeatures.forEach(feature => {
          const status = this.featureStatus.setNewStatus(feature, accessInfo, activatedOptInFeatures);
          this.updateFeatureStatus(feature, status);
        });
      });
    }
    validatePlugin(plugin) {
      if (isPlatformBrowser(this.platformId)) {
        const registeredPlugins = this.pluginRegistry$.value;
        if (registeredPlugins.find(f => f.__info.name === plugin.__info.name)) {
          throw new Error('Feature registration error: A feature named "' + plugin.__info.name + '" is already registered!');
        }
      }
      const info = plugin.__info;
      if (info.type === FeatureType.Dynamic) {
        if (!info.simpleAbilityCheck && !info.requiredScopes?.length) {
          throw new Error('Invalid Feature setup: FeatureType.Dynamic requires at least one of simpleAbilityCheck or requiredScope to be set!');
        }
      }
      if (info.type === FeatureType.OptIn) {
        if (info.scope === OptInFeatureScope.Company && info.configurableBy === OptInFeatureManager.Self) {
          throw new Error('Invalid Feature setup: Company OptIn features can only be controlled by admins!');
        }
      }
    }
    getLoadedPlugins$() {
      return this._loadedPlugins$.asObservable();
    }
    initializePlugins() {
      this.observeEachPlugin().pipe(takeWhileAlive(this.destroyRef)).subscribe(plugin => {
        // listen to incoming status changes for each registered plugin.
        // react to changes by loading/unloading the plugins.
        const status$ = this.featureStatus.get$(plugin);
        status$.pipe(takeWhileAlive(this.destroyRef)).subscribe(status => {
          const {
            isEnabled
          } = this.handleStatusUpdate(plugin, status);
          this.updateLoadedPlugins(plugin, isEnabled);
        });
      });
    }
    updateLoadedPlugins(plugin, isEnabled) {
      const currentLoaded = this._loadedPlugins$.getValue();
      if (isEnabled) {
        if (!currentLoaded.find(p => p === plugin)) {
          const update = currentLoaded.concat(plugin);
          this._loadedPlugins$.next(update);
        }
      } else {
        const index = currentLoaded.findIndex(p => p === plugin);
        if (index >= 0) {
          const update = [...currentLoaded];
          update.splice(index, 1);
          this._loadedPlugins$.next(update);
        }
      }
    }
    handleStatusUpdate(feature, status) {
      let shouldLoad = false;
      if (!isPlatformBrowser(this.platformId)) {
        // in SSR context, only features of type Permanent are allowed.
        // All dynamic / optIn features are user specific and must be disabled.
        shouldLoad = status.enabled && feature.__info.type === FeatureType.Permanent;
      } else {
        shouldLoad = status.enabled;
      }
      if (shouldLoad && !status.loaded) {
        this.updateFeatureStatus(feature, {
          loaded: true
        });
        feature.onFeatureInit();
      } else if (!shouldLoad && status.loaded) {
        this.updateFeatureStatus(feature, {
          loaded: false
        });
        feature.onFeatureDestroy();
      }
      return {
        isEnabled: shouldLoad
      };
    }
    static {
      this.ɵfac = function FrontendFeatureRegistry_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FrontendFeatureRegistry)(i0.ɵɵinject(i1.ConfigService), i0.ɵɵinject(i2.DevToolsRegistry), i0.ɵɵinject(BASE_FEATURES_PROVIDER), i0.ɵɵinject(PLATFORM_ID));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: FrontendFeatureRegistry,
        factory: FrontendFeatureRegistry.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FrontendFeatureRegistry;
})();